import React, { useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Inbox as InboxIcon, Send, ArrowLeft, ShieldCheck, X } from "lucide-react";
import api from "../lib/apiClient";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";

const formatStamp = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (new Date() - d) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return d.toLocaleDateString();
};

const AdminThreadDetail = ({ threadId, onChanged }) => {
  const [thread, setThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [body, setBody] = useState("");
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);
  const [closing, setClosing] = useState(false);
  const bottomRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/contact-messages/${threadId}`);
      setThread(data.thread);
      setMessages(data.messages);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load thread");
      setThread(null);
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [threadId]);
  useEffect(() => {
    if (bottomRef.current) bottomRef.current.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const sendReply = async (e) => {
    e.preventDefault();
    if (!body.trim()) return;
    setBusy(true);
    try {
      await api.post(`/admin/contact-messages/${threadId}/reply`, { message: body.trim() });
      setBody("");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't send reply");
    }
    setBusy(false);
  };

  const closeThread = async () => {
    if (!window.confirm("Close this thread? A user reply will re-open it automatically.")) return;
    setClosing(true);
    try {
      await api.post(`/admin/contact-messages/${threadId}/close`);
      toast.success("Thread closed");
      await load();
      onChanged && onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't close");
    }
    setClosing(false);
  };

  if (loading) return <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>;
  if (!thread) return <p className="text-gray-500 text-sm">Thread not found.</p>;

  return (
    <div className="bg-gray-900 border border-gray-800 rounded-lg flex flex-col" data-testid="admin-thread-detail" style={{ minHeight: "60vh" }}>
      <div className="border-b border-gray-800 p-4">
        <Link to="/admin/inbox" className="text-emerald-400 hover:text-emerald-300 text-xs flex items-center gap-1 md:hidden mb-2">
          <ArrowLeft className="h-3 w-3" /> Back
        </Link>
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <h2 className="text-white font-semibold text-base sm:text-lg truncate" data-testid="admin-thread-subject">{thread.subject}</h2>
            <p className="text-gray-500 text-xs mt-1">
              From{" "}
              <Link to={`/${thread.username}`} className="text-emerald-400 hover:underline" data-testid="admin-thread-user-link">
                /{thread.username}
              </Link>
              {thread.user_email && <span className="text-gray-600"> · {thread.user_email}</span>}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {thread.status === "closed" ? (
              <span className="text-[10px] uppercase tracking-wider bg-gray-800 text-gray-400 px-2 py-0.5 rounded">closed</span>
            ) : (
              <Button size="sm" variant="ghost" onClick={closeThread} disabled={closing} className="text-rose-400 hover:text-rose-300" data-testid="admin-close-thread-btn">
                <X className="h-3.5 w-3.5 mr-1" /> Close
              </Button>
            )}
          </div>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="admin-thread-messages">
        {messages.map((m) => {
          const fromAdmin = m.sender_role === "admin";
          return (
            <div key={m.id} className={`flex ${fromAdmin ? "justify-end" : "justify-start"}`} data-testid={`admin-msg-${m.id}`}>
              <div className={`max-w-[85%] rounded-lg px-3 py-2 text-sm whitespace-pre-wrap break-words ${
                fromAdmin ? "bg-amber-600/20 text-amber-50 border border-amber-600/40" : "bg-gray-800 text-gray-200 border border-gray-700"
              }`}>
                <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 opacity-70">
                  {fromAdmin && <ShieldCheck className="h-3 w-3 text-amber-400" />}
                  <span>{fromAdmin ? `${m.sender_username || "staff"} (admin)` : `/${m.sender_username}`}</span>
                  <span>·</span><span>{formatStamp(m.created_at)}</span>
                </div>
                {m.body}
              </div>
            </div>
          );
        })}
        <div ref={bottomRef} />
      </div>

      <form onSubmit={sendReply} className="border-t border-gray-800 p-3 flex flex-col gap-2" data-testid="admin-thread-reply-form">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type the staff reply…"
          rows={3}
          className="bg-gray-950 border-gray-700 text-white resize-none"
          data-testid="admin-thread-reply-input"
        />
        <div className="flex justify-end">
          <Button type="submit" disabled={busy || !body.trim()} className="bg-amber-600 hover:bg-amber-700" data-testid="admin-thread-reply-submit">
            <Send className="h-4 w-4 mr-1.5" />
            {busy ? "Sending…" : "Reply as staff"}
          </Button>
        </div>
      </form>
    </div>
  );
};

const AdminInboxPage = () => {
  const { user } = useAuth();
  const { threadId } = useParams();
  const navigate = useNavigate();
  const [threads, setThreads] = useState([]);
  const [unreadOnly, setUnreadOnly] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/admin/contact-messages${unreadOnly ? "?unread_only=true" : ""}`);
      setThreads(data.items);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't load admin inbox");
    }
    setLoading(false);
  };

  useEffect(() => {
    if (!user?.is_admin) return;
    load();
    // eslint-disable-next-line
  }, [user, unreadOnly]);

  if (!user) return <Navigate to="/" replace />;
  if (!user.is_admin) {
    return <div className="container mx-auto px-4 py-12 text-center text-gray-400">Admin access required.</div>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-950 to-black text-gray-200" data-testid="admin-inbox-page">
      <div className="container mx-auto px-4 py-8 max-w-6xl">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-2">
            <InboxIcon className="h-6 w-6 text-amber-400" />
            <h1 className="text-2xl sm:text-3xl font-bold text-white">Admin Inbox</h1>
          </div>
          <div className="flex items-center gap-3">
            <label className="text-gray-400 text-sm flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={unreadOnly}
                onChange={(e) => { setUnreadOnly(e.target.checked); navigate("/admin/inbox"); }}
                data-testid="admin-inbox-unread-only-toggle"
              /> Unread only
            </label>
            <Link to="/admin" className="text-emerald-400 hover:text-emerald-300 text-sm">← Console</Link>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-10"><div className="animate-spin h-6 w-6 border-2 border-emerald-500 border-t-transparent rounded-full" /></div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className={`${threadId ? "hidden md:block" : ""} md:col-span-1 space-y-2`} data-testid="admin-thread-list">
              {threads.length === 0 ? (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-6 text-center text-gray-500 text-sm">
                  No threads {unreadOnly ? "are unread." : "yet."}
                </div>
              ) : (
                threads.map((t) => {
                  const unread = (t.unread_for_admin || 0) > 0;
                  const isActive = t.id === threadId;
                  return (
                    <Link
                      key={t.id}
                      to={`/admin/inbox/${t.id}`}
                      className={`block rounded-lg p-3 border transition-colors ${
                        isActive ? "bg-amber-600/15 border-amber-500/50"
                        : unread ? "bg-gray-900 border-amber-500/30 hover:border-amber-500/60"
                        : "bg-gray-900 border-gray-800 hover:border-gray-700"
                      }`}
                      data-testid={`admin-thread-${t.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className={`truncate text-sm ${unread ? "text-white font-semibold" : "text-gray-300"}`}>{t.subject}</p>
                          <p className="text-gray-500 text-[11px] mt-0.5">
                            /{t.username} · {t.status === "closed" ? "Closed · " : ""}{formatStamp(t.last_message_at)}
                          </p>
                        </div>
                        {unread && (
                          <span className="shrink-0 min-w-[20px] h-5 px-1.5 rounded-full bg-amber-500 text-black text-[10px] font-bold flex items-center justify-center">
                            {t.unread_for_admin}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })
              )}
            </div>
            <div className={`${threadId ? "" : "hidden md:block"} md:col-span-2`}>
              {threadId ? (
                <AdminThreadDetail threadId={threadId} onChanged={load} />
              ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-lg p-8 text-center text-gray-500 text-sm h-full flex items-center justify-center" style={{ minHeight: "40vh" }}>
                  Pick a thread on the left.
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInboxPage;
